/**
 * Deterministic randomness for the demo. Every generator seeds from a string
 * (e.g. `incidents:2027-Q2`) so the same page shows the same numbers on every
 * load, and different pages that describe the same thing agree.
 */

function hash(seed: string): number {
    let h = 1779033703 ^ seed.length;
    for (let i = 0; i < seed.length; i++) {
        h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
}

export class Rng {
    private state: number;

    constructor(seed: string) {
        this.state = hash(seed);
    }

    /** Float in [0, 1). mulberry32. */
    next(): number {
        let t = (this.state += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }

    /** Integer in [min, max] inclusive. */
    int(min: number, max: number): number {
        return min + Math.floor(this.next() * (max - min + 1));
    }

    /** Float in [min, max), rounded to `dp` decimal places. */
    float(min: number, max: number, dp = 1): number {
        const f = 10 ** dp;
        return Math.round((min + this.next() * (max - min)) * f) / f;
    }

    bool(pTrue = 0.5): boolean {
        return this.next() < pTrue;
    }

    pick<T>(items: readonly T[]): T {
        return items[Math.floor(this.next() * items.length)];
    }

    /** Pick by relative weights. */
    weighted<T>(items: readonly (readonly [T, number])[]): T {
        const total = items.reduce((s, [, w]) => s + w, 0);
        let r = this.next() * total;
        for (const [item, w] of items) {
            if ((r -= w) < 0) {
                return item;
            }
        }
        return items[items.length - 1][0];
    }

    shuffle<T>(items: readonly T[]): T[] {
        const a = [...items];
        for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(this.next() * (i + 1));
            [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
    }

    sample<T>(items: readonly T[], n: number): T[] {
        return this.shuffle(items).slice(0, Math.min(n, items.length));
    }

    /** Roughly normal (sum of uniforms), clamped to [min, max]. */
    around(mean: number, spread: number, min = -Infinity, max = Infinity, dp = 1): number {
        const n = (this.next() + this.next() + this.next() - 1.5) / 1.5;
        const f = 10 ** dp;
        return Math.min(max, Math.max(min, Math.round((mean + n * spread) * f) / f));
    }
}

export const rng = (seed: string): Rng => new Rng(seed);
